import { useAuth0 } from '@auth0/auth0-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowCounterClockwise, ArrowRight } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import toast from 'react-hot-toast';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import { SubscriptionStatus } from 'src/api/endpoints/storeApi.types';
import { useReactivateCloudConnectSubscription } from 'src/api/queries';
import { Button, Divider, Feedback, Label, Sheet } from 'src/components';
import { EnterpriseSubscriptionCard } from 'src/components/EnterpriseSubscriptionCard';
import { ValueProps } from 'src/components/Offers/ValueProps';
import { StatusTag } from 'src/components/StatusTag';
import { formatDate } from 'src/utils/formatDate';
import { URLS } from 'src/utils/urls/urls';
import { userHelpers } from 'src/utils/userHelpers';
import { valueProps } from 'src/utils/valueProps';

import { CardHeading } from '../../components/CardHeading';
import { CloudConnectCancelSubscriptionButton } from '../../components/CloudConnectCancelSubscriptionButton';
import { FreeConnectDetails } from '../../components/FreeConnectDetails';
import { NoSubscriptionDetails } from '../../components/NoSubscriptionCard';
import {
  FAQVariant,
  SubscriptionFAQContent,
} from '../../components/SubscriptionFAQ';

const routeApi = getRouteApi('/_auth/_layout/subscriptions/plan');

const getStatus = (
  subscription: CloudConnectSubscription
): SubscriptionStatus => {
  if (subscription.cancelAtPeriodEnd || subscription.status === 'canceled') {
    return SubscriptionStatus.Cancelled;
  }

  if (subscription.status === 'trialing') {
    return SubscriptionStatus.Trialing;
  }

  if (
    subscription.status === 'past_due' ||
    subscription.status === 'paused' ||
    subscription.status === 'unpaid'
  ) {
    return SubscriptionStatus.Deactivated;
  }

  return SubscriptionStatus.Active;
};

export const CloudConnectSubscriptionPage = ({
  subscription,
}: {
  subscription: CloudConnectSubscription | null;
}) => {
  const { i18n, t } = useLingui();
  const auth = useAuth0();
  const reactivateSubscription = useReactivateCloudConnectSubscription();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  if (search.cancel_subscription === 'confirmation') {
    toast.success(t`Subscription cancelled`, {
      id: 'subscription-cancelled-toast',
    });
    void navigate({ search: { cancel_subscription: undefined } });
  }

  const shouldShowEnterpriseAccess =
    !subscription && userHelpers.isOrganizationMember(auth.user);

  if (shouldShowEnterpriseAccess) {
    return (
      <div
        className="flex flex-col gap-24 lm:gap-32"
        data-cy="subscription-page"
      >
        <Sheet className="gap-32" data-cy="cloud-connect-subscription-card">
          <EnterpriseSubscriptionCard />
        </Sheet>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div
        className="flex flex-col gap-24 lm:gap-32"
        data-cy="subscription-page"
      >
        <Sheet className="gap-32" data-cy="cloud-connect-subscription-card">
          <NoSubscriptionDetails />
        </Sheet>
        <SubscriptionFAQContent variant={FAQVariant.NO_PLAN} />
      </div>
    );
  }

  if (subscription.productId === 'connect_legacy') {
    return (
      <div
        className="flex flex-col gap-24 lm:gap-32"
        data-cy="subscription-page"
      >
        <Sheet className="gap-32" data-cy="cloud-connect-subscription-card">
          <FreeConnectDetails />
        </Sheet>
        <SubscriptionFAQContent variant={FAQVariant.FREE_CONNECT} />
      </div>
    );
  }

  const activationDate = formatDate(subscription.createdAt, {
    locale: i18n.locale,
  });
  const periodEnd = formatDate(subscription.periodEnd, {
    locale: i18n.locale,
  });
  const endDate =
    subscription.cancelAtPeriodEnd || subscription.status === 'canceled'
      ? `. ${t`Ends ${periodEnd}`}`
      : null;
  const shouldSeePaymentButton =
    !subscription.cancelAtPeriodEnd &&
    (subscription.status === 'active' || subscription.status === 'trialing');
  const canCancel =
    !subscription.cancelAtPeriodEnd &&
    (subscription.status === 'active' ||
      subscription.status === 'trialing' ||
      subscription.status === 'past_due' ||
      subscription.status === 'unpaid');
  const isCancelFlowOpen =
    search.cancel_subscription !== undefined &&
    search.cancel_subscription !== 'confirmation';

  return (
    <div className="flex flex-col gap-24 lm:gap-32" data-cy="subscription-page">
      <Sheet className="gap-32" data-cy="cloud-connect-subscription-card">
        <div data-cy="subscription-card-activated">
          <div className="flex flex-row items-start justify-between gap-8">
            <div>
              <CardHeading>
                <Trans>Connect</Trans>
              </CardHeading>
              <Label
                className="inline-block"
                data-cy="subscription-card-activated-label"
              >
                <Trans>
                  Activated {activationDate} {endDate}
                </Trans>
              </Label>
            </div>

            <StatusTag
              size="fluid"
              status={getStatus(subscription)}
              data-cy="subscription-status-tag"
            />
          </div>

          <Divider className="my-16" />

          <div>
            <Typography variant="body-lg">
              <Trans>
                Enjoy unlimited cloud storage, seamless integrations,
                handwriting search, and a growing list of tools built to suit
                your workflow.
              </Trans>
            </Typography>

            <div
              data-cy="connect-offer-perks"
              className="mt-24 flex flex-col gap-16 md:flex-row"
            >
              <ValueProps
                className="justify-start"
                valueProps={valueProps.connect}
              />
            </div>
          </div>
        </div>

        {(canCancel || isCancelFlowOpen) && (
          <div className="flex flex-wrap gap-24">
            {shouldSeePaymentButton && (
              <Button
                as="a"
                to={URLS.SUBSCRIPTION_PAYMENT}
                variant="primary"
                size="large"
                className="w-full ls:w-fit"
                data-cy="edit-payment-details-btn"
              >
                <span>
                  <Trans>Edit payment details</Trans>
                </span>
                <ArrowRight />
              </Button>
            )}
            <CloudConnectCancelSubscriptionButton />
          </div>
        )}

        {subscription.cancelAtPeriodEnd && (
          <div className="flex flex-wrap gap-24">
            <AbortCancelSubscriptionModal
              isAborting={reactivateSubscription.isPending}
              abortError={reactivateSubscription.error}
              onAbort={() => {
                reactivateSubscription.mutate();
              }}
              trigger={
                <Button
                  variant="primary"
                  size="large"
                  analytics={{
                    text: 'Undo cancelation',
                    location:
                      ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
                    action: 'open undo cancelation modal',
                  }}
                >
                  <ArrowCounterClockwise />
                  <span>
                    <Trans>Undo cancelation</Trans>
                  </span>
                </Button>
              }
            />
          </div>
        )}
      </Sheet>
      <SubscriptionFAQContent
        variant={
          subscription.status === 'canceled'
            ? FAQVariant.CONNECT_CANCELLED
            : FAQVariant.CONNECT
        }
      />
      <Feedback
        feedbackTargetId="connect-plan-page-v1"
        title={t`Did you find what you were looking for?`}
      />
    </div>
  );
};
import { AbortCancelSubscriptionModal } from '../../components/AbortCancelSubscriptionModal';
